import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
    Typography,
    Box,
    TextField,
    Button,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
} from "@mui/material";
import {
    Save,
    Cancel,
    TaskAltRounded,
    AddOutlined,
    CancelRounded,
} from "@mui/icons-material";
import GGButton from "../../../CommonComponents/GGButton/GGButton";
import GGLoaderView from "../../../UtilityComponents/GGLoaderView/GGLoaderView";
import SnackBar, { SnackTypes } from "../../../UtilityComponents/SnackBar/SnackBar";
import UploadImagesSectionDraggable from "../../../../utils/UploadImagesSectionDraggable";
import { ErrorLogger } from "../../../../utils/Logger";
import { PriceTableRow } from "../../AddNewGigPageComponents/PriceTableRow";
import { CategoryAPIHandler } from "../../../../services/NetworkClient/BackendAPIs/CategoryAPIHandler";
import { SubcategoryAPIHandler } from "../../../../services/NetworkClient/BackendAPIs/SubcategoryAPIhandler";
import { Category } from "../../../../Models/Category";
import { Subcategory } from "../../../../Models/SubCategory";
import { SellerAPIHandler } from "../../../../services/NetworkClient/BackendAPIs/SellerAPIhandler";
import { SellerInterface } from "../../../../Models/Seller";
import './GigEditingPage.scss';
import { GigFormDataInterface } from "./GigEditingPage";

export const AddNewGigPage = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const [loading, setLoading] = useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [isScrolled, setIsScrolled] = useState(false);
    const [seller, setSeller] = useState<SellerInterface | null>(null);

    const snackBarRef = useRef<{ showSnackbar(): any }>(null);
    const [snackBarParams, setSnackBarParams] = useState({
        message: "",
        state: SnackTypes.error,
    });

    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const initializedFromStateRef = useRef(false);

    const initGigFormData: GigFormDataInterface = {
        title: "",
        description: "",
        images: [],
        portfolioUpdated: false,
        isListed: true,
        priceTable: [
            {
                minPrice: undefined,
                maxPrice: undefined,
                duration: undefined,
                durationUnit: "days",
                description: "",
            },
        ],
    };

    const [gigFormData, setGigFormData] = useState<GigFormDataInterface>(initGigFormData);
    
    // Category and Subcategory state
    const [categories, setCategories] = useState<Category[]>([]);
    const [subcategories, setSubcategories] = useState<Subcategory[]>([]);
    const [selectedCategoryId, setSelectedCategoryId] = useState<string>("");
    const [selectedSubcategoryId, setSelectedSubcategoryId] = useState<string>("");

    // Fetch categories on mount
    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const response = await CategoryAPIHandler.shared.getAllCategories();
                setCategories(response.categories);
            } catch (error) {
                ErrorLogger.log(error, 'AddNewGigPage.fetchCategories');
                showSnackbar("Failed to load categories. Please refresh the page.", SnackTypes.error);
            }
        };
        fetchCategories();
    }, []);

    useEffect(() => {
        const fetchSeller = async () => {
            try {
                setLoading(true);
                const fetchedSeller = await SellerAPIHandler.shared.getSellerByAuthId();
                setSeller(fetchedSeller);
            } catch (error) {
                ErrorLogger.log(error, 'AddNewGigPage.fetchSeller');
            } finally {
                setLoading(false);
            }
        };

        fetchSeller();
    }, []);

    useEffect(() => {
        if (initializedFromStateRef.current) {
            return;
        }
        const { gigFormData: incomingGigFormData, selectedCategoryId: incomingCategoryId, selectedSubcategoryId: incomingSubcategoryId } = location.state || {};
        if (incomingGigFormData) {
            setGigFormData(incomingGigFormData);
            setHasUnsavedChanges(true);
        }
        if (incomingCategoryId) {
            setSelectedCategoryId(incomingCategoryId);
        }
        if (incomingSubcategoryId) {
            setSelectedSubcategoryId(incomingSubcategoryId);
        }
        initializedFromStateRef.current = true;
    }, [location.state]);

    // Fetch subcategories when category is selected
    useEffect(() => {
        if (selectedCategoryId) {
            const fetchSubcategories = async () => {
                try {
                    const response = await SubcategoryAPIHandler.shared.getSubcategoriesForCategory(
                        selectedCategoryId,
                        1,
                        Number.MAX_SAFE_INTEGER
                    );
                    setSubcategories(response.subcategories);
                    // Reset subcategory selection when category changes
                    setSelectedSubcategoryId("");
                } catch (error) {
                    ErrorLogger.log(error, 'AddNewGigPage.fetchSubcategories');
                    showSnackbar("Failed to load subcategories. Please try again.", SnackTypes.error);
                }
            };
            fetchSubcategories();
        } else {
            setSubcategories([]);
            setSelectedSubcategoryId("");
        }
    }, [selectedCategoryId]);

    // Handle scroll to add shadow to header
    useEffect(() => {
        const handleScroll = () => {
            // The scroll happens in the dashboard-content-scrollable container
            const scrollContainer = document.querySelector('.dashboard-content-scrollable') as HTMLElement;
            if (scrollContainer) {
                const scrollPosition = scrollContainer.scrollTop;
                setIsScrolled(scrollPosition > 10);
            } else {
                // Fallback to window scroll if container not found
                const scrollPosition = window.scrollY || document.documentElement.scrollTop;
                setIsScrolled(scrollPosition > 10);
            }
        };

        // Small delay to ensure DOM is ready
        const timeoutId = setTimeout(() => {
            // Listen to the dashboard content scrollable container
            const scrollContainer = document.querySelector('.dashboard-content-scrollable') as HTMLElement;
            if (scrollContainer) {
                scrollContainer.addEventListener('scroll', handleScroll);
                // Check initial scroll position
                handleScroll();
            }
            
            // Also listen to window scroll as fallback
            window.addEventListener('scroll', handleScroll, true);
        }, 100);

        return () => {
            clearTimeout(timeoutId);
            const scrollContainer = document.querySelector('.dashboard-content-scrollable') as HTMLElement;
            if (scrollContainer) {
                scrollContainer.removeEventListener('scroll', handleScroll);
            }
            window.removeEventListener('scroll', handleScroll, true);
        };
    }, []);

    const showSnackbar = (message: string, state: SnackTypes) => {
        setSnackBarParams({ message: message, state: state });
        snackBarRef.current?.showSnackbar();
    };

    const updateGigFormData = (fieldName: string, value: any) => {
        setGigFormData((prev) => ({ ...prev, [fieldName]: value }));
        setHasUnsavedChanges(true);
    };

    const updatePriceTableRow = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, index: number) => {
        const { name, value } = e.target;
        const updatedPriceTable = [...gigFormData.priceTable];
        updatedPriceTable[index] = {
            ...updatedPriceTable[index],
            [name]: value,
        };
        updateGigFormData("priceTable", updatedPriceTable);
    };

    const deletePriceTableRow = (index: number) => {
        if (gigFormData.priceTable.length > 1) {
            const updatedPriceTable = gigFormData.priceTable.filter((_, i) => i !== index);
            updateGigFormData("priceTable", updatedPriceTable);
        }
    };

    const validateGigFormData = (formData: GigFormDataInterface): boolean => {
        if (formData.images.length < 3) {
            showSnackbar("Gig portfolio needs a minimum of 3 images", SnackTypes.error);
            return false;
        } else if (formData.images.length > 10) {
            showSnackbar("Gig portfolio can have a maximum of 10 images", SnackTypes.error);
            return false;
        }

        if (!formData.title.trim()) {
            showSnackbar("Gig title is required", SnackTypes.error);
            return false;
        }

        if (formData.title.length < 15) {
            showSnackbar("Gig title needs to have at least 15 characters", SnackTypes.error);
            return false;
        }

        if (formData.title.length > 200) {
            showSnackbar("Gig title must be 200 characters or less", SnackTypes.error);
            return false;
        }

        if (!selectedCategoryId) {
            showSnackbar("Gig category is required", SnackTypes.error);
            return false;
        }

        if (!selectedSubcategoryId) {
            showSnackbar("Gig subcategory is required", SnackTypes.error);
            return false;
        }

        if (!formData.description.trim()) {
            showSnackbar("Gig description is required", SnackTypes.error);
            return false;
        }

        if (formData.description.length < 100) {
            showSnackbar("Gig description needs to have at least 100 characters", SnackTypes.error);
            return false;
        }

        if (formData.description.length > 4000) {
            showSnackbar("Gig description must be 4000 characters or less", SnackTypes.error);
            return false;
        }

        // Validate price table
        for (let i = 0; i < formData.priceTable.length; i++) {
            const row = formData.priceTable[i];
            if (!row.minPrice || !row.maxPrice || !row.duration || !row.description.trim()) {
                showSnackbar(`Please fill all fields in Price Range ${i + 1}`, SnackTypes.error);
                return false;
            }
            if (Number(row.minPrice) >= Number(row.maxPrice)) {
                showSnackbar(`Minimum price must be less than maximum price in Price Range ${i + 1}`, SnackTypes.error);
                return false;
            }
            if (row.description.trim().length < 50) {
                showSnackbar(`Description in Price Range ${i + 1} should be a minimum of 50 characters`, SnackTypes.error);
                return false;
            }
            if (row.description.trim().length > 500) {
                showSnackbar(`Description in Price Range ${i + 1} exceeds maximum of 500 characters`, SnackTypes.error);
                return false;
            }
        }

        return true;
    };

    const handleNewGigCreation = async () => {
        if (!validateGigFormData(gigFormData)) {
            return;
        }

        // route to the next sub-page AddNewGigPagePaymentsPage
        let effectiveSeller = seller;
        if (!effectiveSeller) {
            try {
                effectiveSeller = await SellerAPIHandler.shared.getSellerByAuthId();
                setSeller(effectiveSeller);
            } catch (error) {
                ErrorLogger.log(error, 'AddNewGigPage.handleNewGigCreation.fetchSeller');
            }
        }

        if (effectiveSeller?.paymentDetails && effectiveSeller.paymentDetails.length > 0) {
            navigate("/freelancer-dashboard/my-gigs/add-new-gig/undertaking", {
                state: {
                    gigFormData,
                    selectedCategoryId,
                    selectedSubcategoryId,
                    hasExistingPayment: true,
                },
            });
            return;
        }

        navigate("/freelancer-dashboard/my-gigs/add-new-gig/payments", {
            state: {
                gigFormData,
                selectedCategoryId,
                selectedSubcategoryId,
                hasExistingPayment: false,
            },
        });
    }

    const handleCancel = () => {
        if (hasUnsavedChanges) {
            if (!window.confirm("You have unsaved changes. Are you sure you want to leave? All changes will be lost.")) {
                return;
            }
        }
        navigate('/freelancer-dashboard/my-gigs');
    };

    return (
        <div className="new-gig-editing-page" ref={scrollContainerRef}>
            {loading && <GGLoaderView />}

            {/* Header Section */}
            <div className={`gig-edit-header ${isScrolled ? 'scrolled' : ''}`}>
                <div className="header-left">
                    <Typography variant="h5" className="gig-title-header">
                        Step(1/3): Add New Gig | Gig Details
                    </Typography>
                </div>
                <div className="header-right">
                    <div className="vertical-divider"></div>
                    <div className="header-controls">
                        <div className="header-action-buttons">
                            <Button
                                variant="outlined"
                                startIcon={<Cancel />}
                                onClick={handleCancel}
                                className="cancel-btn"
                            >
                                Cancel
                            </Button>
                            <GGButton
                                variant="contained"
                                color="success"
                                startIcon={<Save />}
                                onClick={handleNewGigCreation}
                                className="save-btn"
                                disabled={!hasUnsavedChanges}
                            >
                                Next
                            </GGButton>
                        </div>
                    </div>
                </div>
                {/* Mobile layout: buttons on right only (no toggle for add new) */}
                <div className="mobile-header-layout add-new-gig-mobile">
                    <div className="mobile-action-buttons">
                        <Button
                            variant="outlined"
                            startIcon={<Cancel />}
                            onClick={handleCancel}
                            className="cancel-btn"
                            size="small"
                        >
                            Cancel
                        </Button>
                        <GGButton
                            variant="contained"
                            color="success"
                            startIcon={<Save />}
                            // onClick={handleSave}
                            onClick={handleNewGigCreation}
                            className="save-btn"
                            disabled={!hasUnsavedChanges}
                            size="small"
                        >
                            Create
                        </GGButton>
                    </div>
                </div>
            </div>

            {/* Content Section */}
            <div className="gig-edit-content">
                {/* Listing Status Card */}
                <Box className="status-card">
                    <Box className="status-content">
                        <Box className="status-icon">
                            <TaskAltRounded sx={{ fontSize: 32, color: "#4caf50" }} />
                        </Box>
                        <Box className="status-text">
                            <Typography variant="h6" className="status-title">
                                New Gig
                            </Typography>
                            <Typography variant="body2" className="status-description">
                                Your new gig will be listed once you create it.
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                {/* Portfolio Images Section */}
                <Box className="section-card">
                    <Typography variant="h6" className="section-title">
                        Portfolio Images *
                    </Typography>
                    <Typography variant="body2" className="section-subtitle">
                        Upload 3-10 images showcasing your work. The first image will be used as the cover image.
                    </Typography>
                    <Box className="images-container">
                        <UploadImagesSectionDraggable
                            editMode={true}
                            gigFormData={gigFormData}
                            updateGigFormData={updateGigFormData}
                        />
                    </Box>
                </Box>

                {/* Title Section */}
                <Box className="section-card">
                    <Typography variant="h6" className="section-title">
                        Gig Title *
                    </Typography>
                    <TextField
                        fullWidth
                        value={gigFormData.title}
                        onChange={(e) => updateGigFormData("title", e.target.value)}
                        placeholder="Enter a compelling title for your gig"
                        inputProps={{ maxLength: 200 }}
                        helperText={`${gigFormData.title.length}/200 characters (minimum 15)`}
                        className="gig-title-input"
                    />
                </Box>

                {/* Category and Subcategory Section */}
                <Box className="section-card">
                    <Typography variant="h6" className="section-title">
                        Category & Subcategory *
                    </Typography>
                    <Typography variant="body2" className="section-subtitle">
                        Select the category and subcategory that best describes your service.
                    </Typography>
                    <Box className="category-subcategory-container">
                        <FormControl className="category-select">
                            <InputLabel>Category *</InputLabel>
                            <Select
                                value={selectedCategoryId}
                                onChange={(e) => {
                                    setSelectedCategoryId(e.target.value);
                                    setHasUnsavedChanges(true);
                                }}
                                label="Category *"
                            >
                                {categories.map((category) => (
                                    <MenuItem key={category.id} value={category.id}>
                                        {category.title}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl className="subcategory-select">
                            <InputLabel>Subcategory *</InputLabel>
                            <Select
                                value={selectedSubcategoryId}
                                onChange={(e) => {
                                    setSelectedSubcategoryId(e.target.value);
                                    setHasUnsavedChanges(true);
                                }}
                                label="Subcategory *"
                                disabled={!selectedCategoryId}
                            >
                                {subcategories.map((subcategory) => (
                                    <MenuItem key={subcategory.id} value={subcategory.id}>
                                        {subcategory.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                </Box>

                {/* Description Section */}
                <Box className="section-card">
                    <Typography variant="h6" className="section-title">
                        Description *
                    </Typography>
                    <TextField
                        fullWidth
                        multiline
                        rows={8}
                        value={gigFormData.description}
                        onChange={(e) => updateGigFormData("description", e.target.value)}
                        placeholder="Describe your service in detail..."
                        inputProps={{ maxLength: 4000 }}
                        helperText={`${gigFormData.description.length}/4000 characters`}
                        className="gig-description-input"
                    />
                </Box>

                {/* Price Table Section */}
                <Box className="section-card">
                    <Typography variant="h6" className="section-title">
                        Pricing Ranges *
                    </Typography>
                    <Typography variant="body2" className="section-subtitle">
                        Define different price ranges for your service
                    </Typography>
                    <Box className="price-table-container">
                        {gigFormData.priceTable.map((_, index) => (
                            <Box key={index} className="price-package-wrapper">
                                <Box className="price-package-header">
                                    <Typography variant="body2" className="price-package-title">
                                        Price Range {index + 1}
                                    </Typography>
                                    {gigFormData.priceTable.length > 1 && (
                                        <Box
                                            className="delete-price-package-btn"
                                            onClick={() => deletePriceTableRow(index)}
                                        >
                                            <CancelRounded fontSize="small" />
                                        </Box>
                                    )}
                                </Box>
                                <PriceTableRow
                                    index={index}
                                    gigFormData={gigFormData}
                                    updatePriceTableRow={updatePriceTableRow}
                                    deletePriceTableRow={deletePriceTableRow}
                                    editMode={true}
                                />
                            </Box>
                        ))}
                    </Box>
                    <Button
                        variant="outlined"
                        startIcon={<AddOutlined />}
                        onClick={() => {
                            updateGigFormData("priceTable", [
                                ...gigFormData.priceTable,
                                {
                                    minPrice: undefined,
                                    maxPrice: undefined,
                                    duration: undefined,
                                    durationUnit: "days",
                                    description: "",
                                },
                            ]);
                        }}
                        className="add-price-row-btn"
                    >
                        Add New Price Range
                    </Button>
                </Box>
            </div>

            {/* Snackbar */}
            <SnackBar
                ref={snackBarRef}
                severity={snackBarParams.state}
                alertDuration={5000}
                message={snackBarParams.message}
            />
        </div>
    );
};

